/**
 * Where each seat sits, and which card belongs to whom.
 *
 * Two rules, and the second is the one that matters:
 *
 * 1. **You are always at the bottom.** Every player sees the same table from
 *    their own chair, which is what a real table does and what makes "the
 *    player on my left" mean the same thing to everybody.
 * 2. **A played card sits between its owner and the middle.** A trick rendered
 *    as an anonymous row is the commonest complaint about digital trick-taking
 *    games — at a real table you know whose card is whose by where it lies.
 *
 * Positions come out as fractions of the ring box, so the caller places them
 * with percentages and the geometry stays independent of any pixel size.
 */

export interface SeatPlacement {
  seat: number;
  /** Fraction across and down the ring, 0..1, for the player's avatar. */
  x: number;
  y: number;
  /** The same bearing, closer in: where this seat's played card goes. */
  cardX: number;
  cardY: number;
  /** True for the viewer's own seat, which sits at the bottom. */
  self: boolean;
}

/**
 * How far out from the middle a seat sits, and how far its played card does.
 *
 * The gap between them has to clear half a seat block plus half a card, or the
 * two collide - which they did at the bottom of a short ring, where a player's
 * own card landed on their name.
 */
const SEAT_RADIUS = 0.5;
const CARD_RADIUS = 0.24;

/**
 * Place `count` seats around a ring, rotated so `localSeat` is at the bottom.
 *
 * Going round from the bottom puts the next seat on the viewer's **left**,
 * which is the direction play runs in nearly every game this toolkit targets.
 * A spectator has no chair, so the table is shown unrotated — seat 0 at the
 * bottom — rather than from a seat that is not theirs.
 */
export function placeSeats(count: number, localSeat: number | null): SeatPlacement[] {
  const placements: SeatPlacement[] = [];
  if (count <= 0) return placements;

  const origin = localSeat ?? 0;

  for (let index = 0; index < count; index++) {
    const seat = (origin + index) % count;
    const theta = (index / count) * Math.PI * 2;
    // theta 0 is the bottom; sine runs to the left so play goes clockwise on
    // screen, the way it does round a table.
    const sin = Math.sin(theta);
    const cos = Math.cos(theta);

    placements.push({
      seat,
      x: 0.5 - SEAT_RADIUS * sin,
      y: 0.5 + SEAT_RADIUS * cos,
      cardX: 0.5 - CARD_RADIUS * sin,
      cardY: 0.5 + CARD_RADIUS * cos,
      self: localSeat !== null && seat === localSeat,
    });
  }

  return placements;
}

/** Percentage string for a fraction, for inline positioning. */
export function percent(fraction: number): string {
  return `${String(Math.round(fraction * 10000) / 100)}%`;
}
